/**
 * GST screen panels for filing scope and return integrity.
 * @file src/web/features/admin/components/GstScopePanels.tsx
 *
 * Three things the GST page needs to say out loud, all of which existed only in
 * the API until now:
 *
 * GstinScope which GSTIN is being looked at. A return is filed per
 * GSTIN, so a client registered in two states has two
 * separate returns — showing one without saying which is
 * how the wrong state's data gets filed.
 *
 * CrossCheckPanel Table 12 reconciled against Tables 4/5/6/7. GSTN blocks
 * submission on a mismatch, so it is better seen here than
 * discovered on rejection.
 *
 * ManualEntryPanel the GSTR-3B fields the books cannot determine. They are
 * reported as zero, and a zero that is really "unknown"
 * must not look like a fact.
 */

import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Landmark, Loader2, Link2 } from 'lucide-react';
import { toast } from '@bloomneo/uikit';
import { adminFetch, adminFetchJson } from '../lib/admin-api';

const inr = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface Registration {
  id: string; gstin: string; stateCode: string; stateName: string | null; isPrimary: boolean;
}
interface ScopeResp {
  registration: Registration | null;
  allRegistrations: Registration[];
  multiRegistration: boolean;
  unattributedCount: number;
}

/**
 * The GSTIN picker. Rendered only when the client holds more than one
 * registration — a single-GSTIN client gets no extra chrome, since there is
 * nothing to choose.
 */
export function GstinScope({ entityId, value, onChange }: {
  entityId: string | null;
  value: string | null;
  onChange: (registrationId: string | null) => void;
}) {
  const [scope, setScope] = useState<ScopeResp | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!entityId) return;
    adminFetchJson<ScopeResp>('/api/gst/registrations', {
      query: { entityId, gstRegistrationId: value ?? undefined },
    }).then(setScope).catch(() => setScope(null));
  }, [entityId, value]);
  useEffect(load, [load]);

  if (!scope) return null;

  if (!scope.registration) {
    return (
      <div className="mb-6 flex items-start gap-3 rounded-2xl border border-danger-200 bg-danger-50 px-6 py-4 text-sm text-danger-800">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <p className="font-semibold">No GST registration on file</p>
          <p className="mt-1">
            A return is filed per GSTIN. Add this client's registration under Masters → Company
            before preparing anything — without it, interstate cannot even be determined.
          </p>
        </div>
      </div>
    );
  }

  const attribute = async () => {
    if (!entityId || busy) return;
    setBusy(true);
    try {
      const res = await adminFetch('/api/gst/registrations/attribute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entityId, gstRegistrationId: value ?? scope.registration?.id }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.message || 'Attribution failed');
      toast.success(
        d.attributed
          ? `${d.attributed} document(s) assigned to ${scope.registration?.gstin}`
          : 'Nothing could be assigned automatically',
      );
      load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {scope.multiRegistration && (
        <div className="mb-6 rounded-2xl border border-ink-200 bg-white px-6 py-4">
          <div className="flex flex-wrap items-center gap-3">
            <Landmark className="h-4 w-4 shrink-0 text-ink-400" />
            <span className="text-sm font-semibold text-ink-900">Filing for</span>
            <select
              value={value ?? scope.registration.id}
              onChange={(e) => onChange(e.target.value)}
              className="rounded-xl border border-ink-200 bg-white px-3 py-1.5 font-mono text-sm focus:border-ink-300 focus-visible:outline-none"
            >
              {scope.allRegistrations.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.gstin} — {r.stateName ?? r.stateCode}{r.isPrimary ? ' (primary)' : ''}
                </option>
              ))}
            </select>
            <span className="text-sm text-ink-500">
              This client holds {scope.allRegistrations.length} registrations. Each files its own
              return — the figures below cover only the one selected.
            </span>
          </div>
        </div>
      )}

      {scope.unattributedCount > 0 && (
        <div className="mb-6 flex flex-wrap items-start gap-3 rounded-2xl border border-warn-200 bg-warn-50 px-6 py-4">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warn-600" />
          <div className="min-w-0 flex-1 text-sm text-warn-900">
            <p className="font-semibold">
              {scope.unattributedCount} document(s) are not assigned to any registration
            </p>
            <p className="mt-1">
              They are excluded from every return until assigned — deliberately, because filing one
              state's invoices under another state's GSTIN is worse than filing them late. Assigning
              matches on the supplying company's own GSTIN; where the books do not record one, the
              documents go to the registration selected above.
            </p>
          </div>
          <button
            onClick={attribute}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-full bg-warn-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-warn-500 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
            Assign documents
          </button>
        </div>
      )}
    </>
  );
}

/* ── Table 12 reconciliation ────────────────────────────────────────────── */

interface CrossCheck {
  ok: boolean;
  tolerance: number;
  rows: { field: string; sections: number; hsnSummary: number; diff: number }[];
}

const FIELD_LABEL: Record<string, string> = {
  txval: 'Taxable value', iamt: 'IGST', camt: 'CGST', samt: 'SGST', csamt: 'Cess',
};

export function CrossCheckPanel({ crossCheck }: { crossCheck?: CrossCheck | null }) {
  if (!crossCheck) return null;

  return (
    <div className={`overflow-hidden rounded-2xl border ${crossCheck.ok ? 'border-ok-200' : 'border-danger-200'}`}>
      <div className={`flex items-start gap-3 px-6 py-4 ${crossCheck.ok ? 'bg-ok-50' : 'bg-danger-50'}`}>
        {crossCheck.ok
          ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-ok-600" />
          : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-danger-600" />}
        <div className={`text-sm ${crossCheck.ok ? 'text-ok-900' : 'text-danger-900'}`}>
          <p className="font-semibold">
            {crossCheck.ok
              ? 'HSN summary reconciles with the invoice tables'
              : 'HSN summary does not reconcile with the invoice tables'}
          </p>
          <p className="mt-1">
            {crossCheck.ok
              ? `Table 12 matches Tables 4/5/6/7 within ₹${crossCheck.tolerance}. GSTN checks this before accepting a return.`
              : 'GSTN blocks submission when these differ beyond tolerance. Resolve before filing.'}
          </p>
        </div>
      </div>

      {!crossCheck.ok && (
        <div className="overflow-x-auto bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-100 text-left text-xs uppercase tracking-wide text-ink-400">
                <th className="px-5 py-3 font-medium">Field</th>
                <th className="px-5 py-3 text-right font-medium">Tables 4/5/6/7</th>
                <th className="px-5 py-3 text-right font-medium">Table 12</th>
                <th className="px-5 py-3 text-right font-medium">Difference</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {crossCheck.rows.filter((r) => Math.abs(r.diff) > crossCheck.tolerance).map((r) => (
                <tr key={r.field}>
                  <td className="px-5 py-2.5 text-ink-700">{FIELD_LABEL[r.field] ?? r.field}</td>
                  <td className="px-5 py-2.5 text-right tabular-nums text-ink-600">₹{inr(r.sections)}</td>
                  <td className="px-5 py-2.5 text-right tabular-nums text-ink-600">₹{inr(r.hsnSummary)}</td>
                  <td className="px-5 py-2.5 text-right font-semibold tabular-nums text-danger-600">₹{inr(r.diff)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ── Table 12 / Table 13 ────────────────────────────────────────────────── */

export function HsnSummary({ portal }: { portal: any }) {
  // Inside `hsn`, where the GSTN schema has them. The top-level keys are read
  // too, for a prepared filing saved before the payload was corrected.
  const b2b = portal?.hsn?.hsn_b2b ?? portal?.hsn_b2b ?? [];
  const b2c = portal?.hsn?.hsn_b2c ?? portal?.hsn_b2c ?? [];
  if (!b2b.length && !b2c.length) return null;

  const section = (label: string, rows: any[]) => rows.length > 0 && (
    <div key={label}>
      <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-ink-400">{label}</p>
      <div className="overflow-x-auto rounded-xl border border-ink-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-ink-200 bg-ink-50/60 text-left text-xs uppercase tracking-wide text-ink-400">
              <th className="px-4 py-2.5 font-medium">HSN/SAC</th>
              <th className="px-4 py-2.5 font-medium">Description</th>
              <th className="px-4 py-2.5 font-medium">UQC</th>
              <th className="px-4 py-2.5 text-right font-medium">Qty</th>
              <th className="px-4 py-2.5 text-right font-medium">Rate</th>
              <th className="px-4 py-2.5 text-right font-medium">Taxable</th>
              <th className="px-4 py-2.5 text-right font-medium">Total value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-50">
            {rows.map((h: any) => (
              <tr key={`${h.hsn_sc}-${h.rt}`}>
                <td className="px-4 py-2 font-mono text-xs">{h.hsn_sc || <span className="text-danger-600">missing</span>}</td>
                <td className="max-w-md truncate px-4 py-2 text-ink-600">
                  {h.desc || <span className="text-warn-600">not in HSN master</span>}
                </td>
                <td className="px-4 py-2 font-mono text-xs text-ink-500">{h.uqc}</td>
                <td className="px-4 py-2 text-right tabular-nums text-ink-500">{h.qty}</td>
                <td className="px-4 py-2 text-right tabular-nums">{h.rt}%</td>
                <td className="px-4 py-2 text-right tabular-nums">₹{inr(h.txval)}</td>
                {/* `val` is 0 in the payload by rule for post-April-2021 periods,
                    so the total is shown from the parts that are reported. */}
                <td className="px-4 py-2 text-right tabular-nums">
                  ₹{inr((h.txval ?? 0) + (h.iamt ?? 0) + (h.camt ?? 0) + (h.samt ?? 0) + (h.csamt ?? 0))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="rounded-2xl border border-ink-200 bg-white px-6 py-5">
      <h3 className="text-base font-semibold text-ink-900">Table 12 — HSN summary</h3>
      <p className="mt-1 text-sm text-ink-500">
        Split into B2B and B2C since May 2025. Description comes from the GSTN master; services
        report unit NA with a quantity of 0, as GSTN files a supply that has none. Goods whose unit
        has no GSTN code report OTH.
      </p>
      {section('B2B', b2b)}
      {section('B2C', b2c)}
    </div>
  );
}

export function DocIssueSummary({ portal, unassigned, unassignedDocs, notes }: {
  portal: any; unassigned?: number;
  /** The documents that fit no series, by voucher type, with a few numbers. */
  unassignedDocs?: { type: string; count: number; samples: string[] }[];
  /** What was worked out about each series' numbers — gaps reported as cancelled, and the like. */
  notes?: { series: string; kind: 'gaps' | 'sparse' | 'elsewhere' | 'before-start' | 'unreadable' | 'out-of-sequence'; count: number; numbers: string[]; message: string }[];
}) {
  const cats = portal?.doc_issue?.doc_det ?? [];

  return (
    <div className="rounded-2xl border border-ink-200 bg-white px-6 py-5">
      <h3 className="text-base font-semibold text-ink-900">Table 13 — documents issued</h3>
      <p className="mt-1 text-sm text-ink-500">
        Mandatory since May 2025. Reports every document series issued in the period, including
        cancelled documents that appear in no other table.
      </p>

      {cats.length === 0 ? (
        <p className="mt-4 rounded-xl border border-warn-200 bg-warn-50 px-4 py-3 text-sm text-warn-900">
          No document series defined, so Table 13 is empty and the return cannot be filed.
          Add them under Masters → Series.
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-xl border border-ink-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-200 bg-ink-50/60 text-left text-xs uppercase tracking-wide text-ink-400">
                <th className="px-4 py-2.5 font-medium">Category</th>
                <th className="px-4 py-2.5 font-medium">From</th>
                <th className="px-4 py-2.5 font-medium">To</th>
                <th className="px-4 py-2.5 text-right font-medium">Issued</th>
                <th className="px-4 py-2.5 text-right font-medium">Cancelled</th>
                <th className="px-4 py-2.5 text-right font-medium">Net</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {cats.flatMap((c: any) =>
                c.docs.map((d: any, i: number) => (
                  <tr key={`${c.doc_num}-${i}`}>
                    <td className="px-4 py-2 text-ink-600">{c.doc_num}</td>
                    <td className="px-4 py-2 font-mono text-xs">{d.from || '—'}</td>
                    <td className="px-4 py-2 font-mono text-xs">{d.to || '—'}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{d.totnum}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{d.cancel}</td>
                    <td className="px-4 py-2 text-right font-semibold tabular-nums">{d.net_issue}</td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* What the counts above rest on. A number reported as cancelled because
          it is missing from the books is an inference, and the numbers are
          named so it can be checked against Tally before filing. */}
      {(notes ?? []).map((n, i) => (
        <p key={i} className={`mt-3 text-sm ${n.kind === 'gaps' || n.kind === 'before-start' || n.kind === 'unreadable' || n.kind === 'out-of-sequence' ? 'text-warn-700' : 'text-ink-500'}`}>
          <span className="font-medium">{n.series}:</span> {n.message}
          {n.numbers.length > 0 && n.kind !== 'unreadable' && n.kind !== 'out-of-sequence' && (
            <span className="mt-0.5 block font-mono text-xs text-ink-500">
              {n.numbers.join(', ')}{n.count > n.numbers.length ? ` and ${n.count - n.numbers.length} more` : ''}
            </span>
          )}
        </p>
      ))}

      {unassigned ? (
        <div className="mt-3 text-sm text-warn-700">
          <p>{unassigned} document{unassigned === 1 ? '' : 's'} in this period fit no defined series and {unassigned === 1 ? 'is' : 'are'} not reported above:</p>
          {unassignedDocs && unassignedDocs.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-xs">
              {unassignedDocs.map((g) => (
                <li key={g.type}>
                  <span className="font-medium">{g.count} {g.type}</span>
                  <span className="font-mono text-ink-500"> — {g.samples.join(', ')}{g.count > g.samples.length ? ', …' : ''}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-1 text-xs text-ink-500">Add a series for them under Masters → Document series, or correct the prefix and suffix of an existing one.</p>
        </div>
      ) : null}
    </div>
  );
}

/* ── GSTR-3B fields the books cannot determine ──────────────────────────── */

export function ManualEntryPanel({ items }: { items?: { field: string; label: string; why: string }[] }) {
  if (!items?.length) return null;
  return (
    <div className="rounded-2xl border border-warn-200 bg-warn-50/60 px-6 py-5">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warn-600" />
        <div>
          <h3 className="text-base font-semibold text-warn-900">Reported as zero — needs a human</h3>
          <p className="mt-1 text-sm text-warn-900">
            These are in the return as zero because the books cannot determine them. A zero here is
            not a finding; confirm each on the portal before filing.
          </p>
        </div>
      </div>
      <ul className="mt-4 space-y-2.5">
        {items.map((m) => (
          <li key={m.field} className="text-sm">
            <span className="font-semibold text-warn-900">{m.label}</span>
            <span className="ml-2 font-mono text-[11px] text-warn-700">{m.field}</span>
            <p className="mt-0.5 text-warn-800">{m.why}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}
